import * as React from 'react';
import { Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  Employee,
  EmployeeToBusiness,
  LoadedUser,
  ModalData,
} from '../interfaces';
import SideBarModal from '../components/modal/SideBarModal';
import { createRow, headerCells } from '../utils/table/teamTable';
import ReuseableTable from '../components/table/ReuseableTable';
import FullPageModal from '../components/modal/FullPageModal';
import NewEditArtistForm from '../components/form/NewEditArtistForm';
import OnboardedModal from '../components/modal/OnboardedModal';
import useUser from '../hooks/global/useUser';
import useFeatureGuard from '../hooks/subscriptions/useFeatureGuard';
import { Feature } from '../types/subscriptions';
import useSubscriptionModal from '../hooks/subscriptions/useSubscriptionModal';
import useConfirm from '../hooks/useConfirm';
import PageLayout from '../components/global/PageLayout';
import { createFooterAction, getFullName } from '../helpers/global';
import { getTeamMemberStatus } from '../helpers/employees';
import { isTeamManager, isUserAdminOrManager } from '../utils/global/helper';
import OnboardingBanner from '../components/onboarding/OnboardingBanner';
import OffboardingCampaignPromptHandler from '../components/offboarding/OffboardingCampaignPromptHandler';
import { CampaignEditorProvider } from '../context/CampaignEditorContext';
import useSubscriptionUpsell from '../hooks/subscriptions/useSubscriptionUpsell';
import PendingJoinRequests from '../components/team/PendingJoinRequests';
import useListOffboardings from '../hooks/offboardings/useListOffboardings';
import OffboardingBanner from '../components/offboarding/OffboardingBanner';
import { Offboarding } from '../types/offboardings';
import { CUSTOMER_WEB_URL, ROUTES } from '../constants/global';
import { resendEmployeeInvite } from '../services/employee';

const EMPTY_MESSAGE = 'No employees added yet!';

export default function TeamPage() {
  const confirm = useConfirm();
  const navigate = useNavigate();
  const { user: loadedUser, triggerReload } = useUser<LoadedUser>();

  const [modalData, setModalData] = React.useState<ModalData>();
  const [staffModalOpen, setStaffModalOpen] = React.useState(false);

  const [selectedEmployee, setSelectedEmployee] = React.useState<Employee>();
  const [staffAdded, setStaffAdded] = React.useState(false);
  const [isResendingInvite, setIsResendingInvite] = React.useState(false);

  const { openSubscriptionModal } = useSubscriptionModal();
  const isIndependent = loadedUser?.business.type === 'INDEPENDENT';
  const canManageTeam = useFeatureGuard(Feature.MANAGE_TEAM);
  const { requireUpsell, upsell } = useSubscriptionUpsell(Feature.MANAGE_TEAM);

  const isStudioManager = !!loadedUser && isTeamManager(loadedUser);

  const { data: pendingOffboardings } = useListOffboardings(
    { businessId: loadedUser?.business.id ?? '', pendingOnly: true },
    { enabled: !!loadedUser?.business.id && !!canManageTeam },
  );

  const offboardingByEmployeeId = React.useMemo(() => {
    const map = new Map<string, Offboarding>();
    pendingOffboardings?.forEach(item => {
      if (!map.has(item.employeeId)) map.set(item.employeeId, item);
    });
    return map;
  }, [pendingOffboardings]);

  const selectedEmployeeOffboarding = selectedEmployee
    ? offboardingByEmployeeId.get(selectedEmployee.employeeId) ?? null
    : null;

  const openEmployee = React.useCallback(
    (newModalData: ModalData, employee: Employee) => {
      if (requireUpsell) {
        upsell();
        return;
      }

      if (!canManageTeam) {
        openSubscriptionModal();
        return;
      }

      setSelectedEmployee(employee);
      setModalData(newModalData);
    },
    [canManageTeam, openSubscriptionModal, requireUpsell, upsell],
  );

  const employeeToBusiness = React.useMemo(() => {
    if (!selectedEmployee) return null;
    const employeeToBus = (loadedUser?.business.employees.find(
      e => e.employeeId === selectedEmployee.employeeId,
    ) ??
      selectedEmployee.employeesToBusinesses?.find(
        eb => eb.businessId === loadedUser?.business.id,
      )) as EmployeeToBusiness;
    return employeeToBus ?? null;
  }, [
    loadedUser?.business.employees,
    loadedUser?.business.id,
    selectedEmployee,
  ]);

  const isSelectedEmployeeInvited =
    !!employeeToBusiness &&
    getTeamMemberStatus(employeeToBusiness, selectedEmployeeOffboarding) ===
      'INVITED';

  const onResendInvite = React.useCallback(async () => {
    if (!loadedUser || !employeeToBusiness || isResendingInvite) return;

    setIsResendingInvite(true);
    try {
      await resendEmployeeInvite({
        employeeId: employeeToBusiness.employeeId,
        businessId: loadedUser.business.id,
      });
      toast.success(
        `Invite resent to ${
          employeeToBusiness.user?.firstName ||
          employeeToBusiness.displayName ||
          'team member'
        }`,
      );
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : 'Cannot resend invite',
      );
      triggerReload();
    } finally {
      setIsResendingInvite(false);
    }
  }, [employeeToBusiness, isResendingInvite, loadedUser, triggerReload]);

  const onBeginOffboard = React.useCallback(() => {
    if (!selectedEmployee || !canManageTeam) return;
    navigate(`${ROUTES.team}/${selectedEmployee.employeeId}/offboard`);
  }, [canManageTeam, navigate, selectedEmployee]);

  const openNewStaff = () => {
    if (requireUpsell) {
      upsell();
      return;
    }

    if (!canManageTeam) {
      openSubscriptionModal();
      return;
    }
    setStaffModalOpen(true);
  };

  const employeeRows = loadedUser?.business.employees.map(employee =>
    createRow({
      currentUser: loadedUser,
      employee,
      handleClick: openEmployee,
      offboarding: offboardingByEmployeeId.get(employee.employeeId) ?? null,
    }),
  );

  const hasPendingOffboarding = !!selectedEmployeeOffboarding;

  const footerActions = React.useMemo(() => {
    const actions = [
      createFooterAction(
        'Edit',
        canManageTeam ? () => setStaffModalOpen(true) : openSubscriptionModal,
      ),
    ];
    if (isSelectedEmployeeInvited) {
      actions.push(
        createFooterAction(
          isResendingInvite ? 'Sending invite…' : 'Resend invite',
          onResendInvite,
        ),
      );
    }
    if (!hasPendingOffboarding) {
      actions.push(
        createFooterAction(
          'Begin offboarding',
          canManageTeam ? onBeginOffboard : openSubscriptionModal,
        ),
      );
    }
    return actions;
  }, [
    canManageTeam,
    hasPendingOffboarding,
    isResendingInvite,
    isSelectedEmployeeInvited,
    onBeginOffboard,
    onResendInvite,
    openSubscriptionModal,
  ]);

  const onCloseStaffModal = async () => {
    if (selectedEmployee) {
      const confirmed = await confirm({
        title: 'Exit without saving?',
        message:
          'Are you sure you want to exit without saving? Any changes you made will be lost.',
        positiveText: 'Exit',
      });
      if (!confirmed) return;
    }

    setStaffModalOpen(false);
  };

  return (
    <CampaignEditorProvider>
      <PageLayout
        title="Team"
        guard={{
          isAllowed:
            isIndependent ||
            (loadedUser?.canManageTeam && isUserAdminOrManager(loadedUser)),
        }}
        topSection={<OnboardingBanner step="STAFF_ONBOARD" />}>
        <div className="header-right">
          <Button
            variant="contained"
            className="primary"
            onClick={openNewStaff}>
            Add New
          </Button>
        </div>

        {isStudioManager && <PendingJoinRequests onSuccess={triggerReload} />}

        <ReuseableTable
          name="All Users"
          rows={employeeRows || []}
          headerCells={headerCells}
          emptyMessage={EMPTY_MESSAGE}
          loading={false}
        />

        {modalData && selectedEmployee && (
          <SideBarModal
            modalOpen={!!selectedEmployee}
            setModalOpen={() => setSelectedEmployee(undefined)}
            name={modalData.name}
            sections={modalData.sections}
            footerActions={footerActions}
            topContent={
              selectedEmployeeOffboarding && loadedUser ? (
                <OffboardingBanner
                  offboarding={selectedEmployeeOffboarding}
                  employeeId={selectedEmployee.employeeId}
                  employeeName={
                    selectedEmployee.displayName ||
                    getFullName({
                      firstName: selectedEmployee.user?.firstName,
                      lastName: selectedEmployee.user?.lastName,
                    }) ||
                    'this artist'
                  }
                  businessId={loadedUser.business.id}
                  onCompleted={() => {
                    setSelectedEmployee(undefined);
                    triggerReload();
                  }}
                />
              ) : null
            }
          />
        )}
      </PageLayout>
      <FullPageModal
        modalOpen={staffModalOpen}
        setModalOpen={onCloseStaffModal}
        name={selectedEmployee ? 'Edit user' : 'Add a user'}>
        <NewEditArtistForm
          setModalOpen={setStaffModalOpen}
          onSuccess={triggerReload}
          setStaffAdded={setStaffAdded}
          setSelectedEmployee={setSelectedEmployee}
          employee={selectedEmployee}
          employeeToBusiness={employeeToBusiness}
        />
      </FullPageModal>

      <OnboardedModal
        open={staffAdded}
        setOpen={setStaffAdded}
        bookingLink={`${CUSTOMER_WEB_URL.replace(
          /^https?:\/\//,
          '',
        )}/artist/${selectedEmployee?.urlPath}`}
        firstName={selectedEmployee?.user?.firstName || 'Your new user'}
        phoneNumber={selectedEmployee?.user?.phoneNumber || ''}
      />

      <OffboardingCampaignPromptHandler />
    </CampaignEditorProvider>
  );
}
